import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Container as ContainerIcon,
  Truck,
  ShoppingBag,
  AlertTriangle,
  ShieldAlert,
  TrendingDown,
  Search,
  ArrowLeftRight,
  Building2,
  LogOut,
  Menu,
  X,
  Settings as SettingsIcon,
  BarChart3,
  Boxes,
  ClipboardCheck,
  Wrench,
} from 'lucide-react';
import { useAuth, isContainerSubpageAllowed } from '../lib/auth';
import { cn } from '../lib/cn';
import SwitchRealmButton from './SwitchRealmButton';
import BrandMark from './BrandMark';

/**
 * Sidebar shell for the IMS Containers module.
 *
 * IMS Containers and Spares are TWO SEPARATE SYSTEMS that happen to share
 * an auth store. They are NOT interconnected — there is no cross-product
 * navigation surface. A user who logged in on the Containers tab can only
 * Logout (not "Switch to Spares"). Conversely the Spares sidebar exposes
 * no Containers link. Each product is its own walled garden.
 *
 * Each NavLink routes to /ims/{section}; the rendered page is a thin
 * wrapper that mounts the shared ImsContainers component with the
 * matching `initialTab` and `hideTabBar` so the in-page tab strip doesn't
 * duplicate the sidebar.
 */
/** Sidebar items.
 *
 *  - `pageKey` is the catalog entry that grants this tab via Page Access.
 *    Hidden for non-admin users whose `allowedPages` doesn't include it
 *    (and doesn't include the top-level `ims-containers` shortcut).
 *  - `subpageKey` ties the item to a category-level hide flag in
 *    containerAccess (e.g. `ims:vendors` -> hideVendor). Independent of
 *    the per-tab grant; either mechanism can hide the tab.
 *  - `adminOnly` means the item renders only for `role === 'admin'`
 *    (used for Settings so Ops users don't see user management). */
/** `hidden` keeps the ROUTE working (deep links, redirects, existing
 *  bookmarks) while removing the button from the rail — the operator asked
 *  for these to disappear from the sidebar, not to be deleted. Flip back to
 *  false to restore a row. */
const NAV_ITEMS: ReadonlyArray<{ to: string; label: string; icon: any; pageKey?: string; subpageKey?: string; adminOnly?: boolean; hidden?: boolean }> = [
  { to: '/ims/live-stock',   label: 'Live Stock',       icon: Boxes,          pageKey: 'ims:live-stock' },
  { to: '/ims/finder',       label: 'Find a container', icon: Search,         pageKey: 'ims:finder' },
  { to: '/ims/overview',     label: 'Overview',         icon: ContainerIcon,  pageKey: 'ims:overview' },
  { to: '/ims/on-lease',     label: 'On Lease',         icon: Truck,          pageKey: 'ims:on-lease',     hidden: true },
  { to: '/ims/sold',         label: 'Sold',             icon: ShoppingBag,    pageKey: 'ims:sold',         subpageKey: 'ims:procurement' },
  { to: '/ims/off-lease',    label: 'Returned',         icon: AlertTriangle,  pageKey: 'ims:off-lease',    hidden: true },
  { to: '/ims/transactions', label: 'Movements',        icon: ArrowLeftRight, pageKey: 'ims:transactions' },
  { to: '/ims/exchange',     label: 'Exchange of Machine', icon: Wrench,      pageKey: 'ims:exchange' },
  { to: '/ims/financials',   label: 'Money',            icon: ShoppingBag,    pageKey: 'ims:financials',   subpageKey: 'ims:financials', hidden: true },
  { to: '/ims/vendors',      label: 'Suppliers',        icon: Building2,      pageKey: 'ims:vendors',      subpageKey: 'ims:vendors' },
  { to: '/ims/exceptions',   label: 'Things to fix',    icon: ShieldAlert,    pageKey: 'ims:exceptions' },
  { to: '/ims/reconciliation', label: 'Verification', icon: ClipboardCheck, pageKey: 'ims:reconciliation' },
  { to: '/ims/aging',        label: 'Sitting time',     icon: TrendingDown,   pageKey: 'ims:aging' },
  { to: '/ims/stock-health', label: 'Stock Health',     icon: ShieldAlert,    pageKey: 'ims:stock-health' },
  { to: '/ims/analytics',    label: 'Container Analytics', icon: BarChart3,   pageKey: 'ims:analytics' },
  { to: '/ims/settings',     label: 'Settings',         icon: SettingsIcon,   adminOnly: true },
];

/** Per-user page-grant check for IMS sidebar items.
 *
 *  - Admins always pass.
 *  - allowedPages == null → legacy "full access" sentinel, pass.
 *  - allowedPages includes the top-level 'ims-containers' key → pass
 *    (shortcut so an admin can grant the whole IMS module in one toggle).
 *  - allowedPages includes the item's pageKey → pass. */
function isImsPageAllowed(user: any, pageKey: string | undefined): boolean {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (!pageKey) return true;
  const allowed = user.allowedPages;
  if (allowed == null) return true; // legacy full access
  if (!Array.isArray(allowed)) return true;
  return allowed.includes('ims-containers') || allowed.includes(pageKey);
}

export default function ContainersLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="min-h-screen md:flex">
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-white border-b border-slate-200 px-4 py-3">
        <button onClick={() => setOpen(true)} className="p-2 -ml-2 rounded-md hover:bg-slate-100" aria-label="Open menu">
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <BrandMark size={26} />
          <span className="font-display text-sm font-extrabold">IMS Containers</span>
        </div>
        <div className="w-8" />
      </header>

      {open && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} aria-hidden />
      )}

      <aside
        className={cn(
          'bg-white border-r border-slate-200 flex shrink-0 flex-col',
          // Mobile: slide-in drawer.
          'fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] transition-transform duration-200',
          open ? 'translate-x-0' : '-translate-x-full',
          // Desktop: stay fixed to the viewport, always full height.
          // Plain `position: fixed` avoids the flex-stretch quirks that
          // were making `sticky` scroll away with the page on long pages.
          // `<main>` below compensates with `md:ml-60` so content isn't
          // hidden under the sidebar.
          'md:sticky md:top-0 md:h-screen md:w-60 md:max-w-none md:translate-x-0'
        )}
      >
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMark size={28} />
            <div>
              <div className="font-display text-sm font-extrabold">IMS Containers</div>
              <div className="text-xs text-slate-500">Crystal Logistic</div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="md:hidden p-1.5 rounded-md text-slate-500 hover:bg-slate-100" aria-label="Close menu">
            <X size={18} />
          </button>
        </div>

        <nav className="p-2 flex-1 overflow-y-auto">
          <div className="space-y-1">
            {NAV_ITEMS
              // 0. Rows the operator asked to take off the rail. The route
              //    still resolves — this only hides the button.
              .filter((item) => !item.hidden)
              // 1. Per-tab page grant (allowedPages) — non-admins only see
              //    tabs whose pageKey is explicitly granted (or whose
              //    `ims-containers` top-level grant is set).
              .filter((item) => isImsPageAllowed(user, item.pageKey))
              // 2. Category-level hide flags (hideVendor / hideProcurement /
              //    hidePricing). Defense in depth on top of allowedPages.
              .filter((item) => !item.subpageKey || isContainerSubpageAllowed(user, item.subpageKey))
              // 3. adminOnly items (Settings) hidden for non-admin users.
              .filter((item) => !item.adminOnly || user?.role === 'admin')
              .map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      'navitem',
                      isActive && 'on'
                    )
                  }
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </NavLink>
              ))}
          </div>

        </nav>

        <div className="p-3 border-t border-slate-200">
          {user && (
            <div className="text-xs text-slate-500 mb-2 truncate" title={user.email}>
              Signed in as<br />
              <span className="text-slate-800 font-medium">{user.name}</span>
            </div>
          )}
          <div className="mb-2"><SwitchRealmButton variant="light" /></div>
          <button onClick={onLogout} className="navitem">
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Push main content right of the fixed sidebar on md+. On mobile
          the sidebar is a drawer so no offset is needed. */}
      <main className="min-w-0 flex-1 bg-slate-50 p-3 sm:p-4 lg:p-6">
        {/* Wide cap, not a narrow column: the realm's tables are 10+ columns and
            should fan out into the space rather than side-scroll. */}
        <div className="mx-auto w-full max-w-[1800px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
